import React, { useState, useRef } from 'react';
import { AlertCircleIcon, CheckCircleIcon, DownloadIcon, FileTextIcon, UploadIcon, XCircleIcon } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { PageHero } from '../components/layout/PageHero';
import { FormStepper } from '../components/supplier/FormStepper';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { countries } from '../data/countries';
import { useScreenInit } from '../useScreenInit.js';
const FORMSUBMIT_ENDPOINT = 'https://formsubmit.co/ajax/procurement@aucommission.org';
const MAX_TOTAL_FILE_SIZE_MB = 10;
type DocKey = 'incorporation' | 'businessRegistration' | 'companyProfile' | 'proofOfAddress' | 'taxRegistration' | 'bankConfirmation' | 'professionalCapability' | 'governmentId' | 'passportPhoto';
type FormData = {
  companyName: string;
  parentCompany: string;
  address: string;
  phone: string;
  whatsappNumber: string;
  email: string;
  contactFullName: string;
  contactTitle: string;
  typeOfOrganisation: string;
  yearEstablished: string;
  registrationNumber: string;
  contractInterest: string;
  categories: string[];
  otherCategory: string;
  areaOfSpecialization: string;
  professionalDiscipline: string;
  qualificationsCertifications: string;
  yearsRelevantExperience: string;
  assignmentOneClientCountry: string;
  assignmentOnePeriod: string;
  assignmentOneScopeValue: string;
  assignmentTwoClientCountry: string;
  assignmentTwoPeriod: string;
  assignmentTwoScopeValue: string;
  assignmentThreeClientCountry: string;
  assignmentThreePeriod: string;
  assignmentThreeScopeValue: string;
  keyPersonnelExpertise: string;
  employeesTotal: string;
  employeesFullTime: string;
  employeesPartTime: string;
  authRepName: string;
  authRepTitle: string;
  authRepPhone: string;
  authRepEmail: string;
  accountName: string;
  bankName: string;
  bankAddress: string;
  accountNumber: string;
  iban: string;
  swiftBic: string;
  currency: string;
  auditedFinancials: string;
  turnoverCurrent: string;
  turnoverPrevious: string;
  bidSecurityAbility: string;
  bidSecurityType: string;
  complianceRepName: string;
  complianceRepNationality: string;
  complianceRepDOB: string;
  complianceRepPlaceOfBirth: string;
  complianceRepIdNumber: string;
  complianceRepCountryOfIssue: string;
  complianceRepFathersName: string;
  complianceRepMothersName: string;
  complianceRepAddress: string;
  complianceRepCountry: string;
  complianceRepPhone: string;
  complianceRepEmail: string;
  complianceRepPosition: string;
  confirmAccurate: boolean;
  agreeCodeOfConduct: boolean;
  understandFalseInfo: boolean;
  declarationName: string;
  declarationTitle: string;
  declarationDate: string;
  signature: string;
};
type TextKey = Exclude<keyof FormData, 'categories' | 'confirmAccurate' | 'agreeCodeOfConduct' | 'understandFalseInfo'>;
type DocumentDef = {
  key: DocKey;
  label: string;
  required: boolean;
  accept: string;
};
const emptyForm: FormData = {
  companyName: '',
  parentCompany: '',
  address: '',
  phone: '',
  whatsappNumber: '',
  email: '',
  contactFullName: '',
  contactTitle: '',
  typeOfOrganisation: '',
  yearEstablished: '',
  registrationNumber: '',
  contractInterest: '',
  categories: [],
  otherCategory: '',
  areaOfSpecialization: '',
  professionalDiscipline: '',
  qualificationsCertifications: '',
  yearsRelevantExperience: '',
  assignmentOneClientCountry: '',
  assignmentOnePeriod: '',
  assignmentOneScopeValue: '',
  assignmentTwoClientCountry: '',
  assignmentTwoPeriod: '',
  assignmentTwoScopeValue: '',
  assignmentThreeClientCountry: '',
  assignmentThreePeriod: '',
  assignmentThreeScopeValue: '',
  keyPersonnelExpertise: '',
  employeesTotal: '',
  employeesFullTime: '',
  employeesPartTime: '',
  authRepName: '',
  authRepTitle: '',
  authRepPhone: '',
  authRepEmail: '',
  accountName: '',
  bankName: '',
  bankAddress: '',
  accountNumber: '',
  iban: '',
  swiftBic: '',
  currency: '',
  auditedFinancials: '',
  turnoverCurrent: '',
  turnoverPrevious: '',
  bidSecurityAbility: '',
  bidSecurityType: '',
  complianceRepName: '',
  complianceRepNationality: '',
  complianceRepDOB: '',
  complianceRepPlaceOfBirth: '',
  complianceRepIdNumber: '',
  complianceRepCountryOfIssue: '',
  complianceRepFathersName: '',
  complianceRepMothersName: '',
  complianceRepAddress: '',
  complianceRepCountry: '',
  complianceRepPhone: '',
  complianceRepEmail: '',
  complianceRepPosition: '',
  confirmAccurate: false,
  agreeCodeOfConduct: false,
  understandFalseInfo: false,
  declarationName: '',
  declarationTitle: '',
  declarationDate: '',
  signature: ''
};
const companyDocuments: DocumentDef[] = [{
  key: 'incorporation',
  label: 'Certificate of Incorporation',
  required: true,
  accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png'
}, {
  key: 'businessRegistration',
  label: 'Business Registration Certificate',
  required: true,
  accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png'
}, {
  key: 'companyProfile',
  label: 'Company Profile',
  required: false,
  accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png'
}, {
  key: 'proofOfAddress',
  label: 'Proof of Registered Address',
  required: false,
  accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png'
}, {
  key: 'taxRegistration',
  label: 'Tax Registration Certificate',
  required: false,
  accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png'
}, {
  key: 'bankConfirmation',
  label: 'Bank Confirmation Letter',
  required: false,
  accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png'
}, {
  key: 'professionalCapability',
  label: 'Capability Statement, CVs or Supporting Portfolio',
  required: false,
  accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png'
}];
const identityDocuments: DocumentDef[] = [{
  key: 'governmentId',
  label: 'Government-Issued Identification (Passport / National ID)',
  required: true,
  accept: '.pdf,.jpg,.jpeg,.png'
}, {
  key: 'passportPhoto',
  label: 'Passport-Sized Photograph',
  required: true,
  accept: '.jpg,.jpeg,.png'
}];
const allDocuments = [...companyDocuments, ...identityDocuments];
const inputClass = 'w-full rounded-lg border border-gray-border bg-white px-4 py-2.5 text-charcoal focus:outline-none focus:ring-2 focus:ring-au-green focus:border-transparent';
function downloadBlankEOIForm() {
  const doc = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait'
  });
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const margin = 18;
  let y = 34;
  const ensure = (space: number) => {
    if (y + space > height - 16) {
      doc.addPage();
      y = 20;
    }
  };
  const heading = (text: string) => {
    ensure(14);
    doc.setTextColor(0, 104, 55);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11.5);
    doc.text(text, margin, y);
    doc.setDrawColor(252, 209, 22);
    doc.setLineWidth(0.8);
    doc.line(margin, y + 1.8, margin + 27, y + 1.8);
    y += 9;
  };
  const line = (label: string) => {
    ensure(9);
    doc.setTextColor(26, 31, 46);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.text(label, margin, y);
    const start = Math.min(margin + doc.getTextWidth(label) + 3, width - margin - 10);
    doc.setDrawColor(160, 160, 160);
    doc.setLineWidth(0.2);
    doc.line(start, y + 1, width - margin, y + 1);
    y += 9;
  };
  const check = (label: string) => {
    ensure(7);
    doc.setDrawColor(26, 31, 46);
    doc.rect(margin, y - 3.4, 3.6, 3.6);
    doc.setTextColor(26, 31, 46);
    doc.setFontSize(9.5);
    doc.text(label, margin + 6, y);
    y += 7;
  };
  doc.setFillColor(0, 104, 55);
  doc.rect(0, 0, width, 24, 'F');
  doc.setFillColor(252, 209, 22);
  doc.rect(0, 24, width, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('AFRICAN UNION COMMISSION', margin, 11);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text('Procurement Department', margin, 17);
  doc.setTextColor(26, 31, 46);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('EXPRESSION OF INTEREST (EOI) FORM', margin, y);
  y += 10;
  heading('SECTION 1: GENERAL INFORMATION');
  ['1. Legal Name of Organisation / Individual Consultant:', '1.1 Parent Organisation (if applicable):', '2. Full Address / Principal Place of Business:', '3. Telephone Number (including country code):', '4. WhatsApp Number:', '5. Email Address:', '6. Name and Title of Contact Person:', '7. Type of Organisation / Applicant:', '8. Year Established / Professional Practice Commenced:', '9. Company or Professional Registration Number:'].forEach(line);
  ensure(8);
  doc.setTextColor(26, 31, 46);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.text('10. Type of Opportunity / Contract Interest (tick one):', margin, y);
  y += 7;
  ['Goods supply', 'Works', 'Consultancy services', 'Non-consulting services', 'Other'].forEach(check);
  heading('SECTION 2: ORGANISATION, BUSINESS & CAPABILITY INFORMATION');
  ['Manufacturer', 'Trading Company', 'Service Provider', 'Contractor', 'Technology Company', 'Supplier', 'Authorized Agent', 'Individual Consultant / Sole Practitioner', 'Other'].forEach(check);
  ['Area of Specialization:', 'Number of Employees — Total / Full Time / Part Time:', 'Authorised Representative — Name:', 'Authorised Representative — Title:', 'Authorised Representative — Telephone:', 'Authorised Representative — Email:'].forEach(line);
  heading('PROFESSIONAL SERVICES CAPABILITY');
  ['Service / Consulting Discipline:', 'Relevant Qualifications and Certifications:', 'Years of Relevant Experience:', 'Similar Assignment 1 — Client / Country:', 'Similar Assignment 1 — Period:', 'Similar Assignment 1 — Scope / Contract Value:', 'Similar Assignment 2 — Client / Country:', 'Similar Assignment 2 — Period:', 'Similar Assignment 2 — Scope / Contract Value:', 'Similar Assignment 3 — Client / Country:', 'Similar Assignment 3 — Period:', 'Similar Assignment 3 — Scope / Contract Value:', 'Key Personnel / Expertise:'].forEach(line);
  check('Capability Statement, CVs or Supporting Portfolio attached (optional)');
  heading('SECTION 3: FINANCIAL & BANKING INFORMATION');
  ['Account Name:', 'Bank Name:', 'Bank Address:', 'Account Number:', 'IBAN:', 'SWIFT/BIC:', 'Currency of Account:', 'Audited Financial Statements Available:', 'Annual Turnover — Current Year:', 'Annual Turnover — Previous Year:'].forEach(line);
  heading('SUPPORTING DOCUMENTATION');
  ['Certificate of Incorporation / Professional Registration (required):', 'Business Registration Certificate (required):', 'Company Profile (if applicable):', 'Proof of Registered Address:', 'Tax Registration Certificate:', 'Bank Confirmation Letter:', 'Capability Statement, CVs or Supporting Portfolio (optional):'].forEach(line);
  heading('SECTION 4: COMPLIANCE & SECURITY REQUIREMENTS');
  ['Ability to provide Bid Security (Yes / No):', 'Type of Bid Security:', 'Full Legal Name:', 'Nationality:', 'Date of Birth:', 'Place of Birth:', 'National Identification / Passport Number:', 'Country of Issue:', "Father's Full Name:", "Mother's Full Name:", 'Residential / Business Address:', 'Country of Residence:', 'Mobile Telephone Number:', 'Email Address:', 'Current Occupation / Position:'].forEach(line);
  check('Government-Issued Identification attached');
  check('Passport-Sized Photograph attached');
  heading('DECLARATION');
  ['Name:', 'Title:', 'Date:', 'Signature:', 'Company Stamp / Seal:'].forEach(line);
  doc.save('AU-Expression-of-Interest-Form.pdf');
}
export function SupplierRegistration() {
  const screenInit = useScreenInit() as {
    currentStep?: number;
  };
  const initialStep = Math.min(5, Math.max(1, screenInit.currentStep ?? 1));
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [data, setData] = useState<FormData>(emptyForm);
  const [files, setFiles] = useState<Record<DocKey, File | null>>({
    incorporation: null,
    businessRegistration: null,
    companyProfile: null,
    proofOfAddress: null,
    taxRegistration: null,
    bankConfirmation: null,
    professionalCapability: null,
    governmentId: null,
    passportPhoto: null
  });
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [whatsappError, setWhatsappError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRefs = useRef<Record<DocKey, HTMLInputElement | null>>({
    incorporation: null,
    businessRegistration: null,
    companyProfile: null,
    proofOfAddress: null,
    taxRegistration: null,
    bankConfirmation: null,
    professionalCapability: null,
    governmentId: null,
    passportPhoto: null
  });
  const steps = ['General Information', 'Business Information', 'Financial & Banking', 'Compliance & Security', 'Declaration & Submit'];
  const update = (key: TextKey, value: string) => {
    setData(previous => ({
      ...previous,
      [key]: value
    }));
  };
  const normalizePhone = (value: string) => {
    const compact = value.trim().replace(/[\s\-().]/g, '');
    if (compact.startsWith('00')) return `+${compact.slice(2)}`;
    if (!compact.startsWith('+') && /^\d{8,15}$/.test(compact)) return `+${compact}`;
    return compact;
  };
  const attachmentSize = () => Object.values(files).reduce((total, file) => total + (file?.size ?? 0), 0) / 1048576;
  const validateStep = () => {
    setError(null);
    if (currentStep === 1) {
      const required: [string, string][] = [[data.companyName, 'Name of Company'], [data.address, 'Full Address of the Company'], [data.email, 'Email Address'], [data.contactFullName, 'Name of Contact Person'], [data.contactTitle, 'Title of Contact Person'], [data.typeOfOrganisation, 'Type of Organisation'], [data.yearEstablished, 'Year Established'], [data.registrationNumber, 'Company Registration Number'], [data.contractInterest, 'Type of opportunity / contract interest']];
      const missing = required.filter(([value]) => !value.trim()).map(([, label]) => label);
      const phone = normalizePhone(data.phone);
      if (!/^\+\d{8,15}$/.test(phone)) {
        setPhoneError('Enter a valid number in international format, for example +447700900123.');
        return false;
      }
      setPhoneError(null);
      if (phone !== data.phone) update('phone', phone);
      if (!data.whatsappNumber.trim()) {
        setWhatsappError('Please provide a WhatsApp number. If you do not use WhatsApp, enter your telephone number here instead.');
        return false;
      }
      setWhatsappError(null);
      if (missing.length) {
        setError(`Please complete: ${missing.join(', ')}.`);
        return false;
      }
      if (!data.contractInterest) {
        setError('Please select the Type of opportunity / contract interest.');
        return false;
      }
    }
    if (currentStep === 2) {
      if (!data.categories.length) {
        setError('Please select at least one Business Category.');
        return false;
      }
      if (data.categories.includes('Other') && !data.otherCategory.trim()) {
        setError('Please specify the Other Business Category.');
        return false;
      }
      if (!data.areaOfSpecialization.trim()) {
        setError('Please provide the Area of Specialization.');
        return false;
      }
      const isProfessionalServices = data.contractInterest === 'Consultancy services' || data.contractInterest === 'Non-consulting services';
      if (isProfessionalServices) {
        const requiredProfessionalFields: [string, string][] = [[data.professionalDiscipline, 'Service / consulting discipline'], [data.qualificationsCertifications, 'Relevant qualifications and certifications'], [data.yearsRelevantExperience, 'Years of relevant experience'], [data.assignmentOneClientCountry, 'First similar assignment client / country'], [data.assignmentOnePeriod, 'First similar assignment period'], [data.assignmentOneScopeValue, 'First similar assignment scope / value'], [data.keyPersonnelExpertise, 'Key personnel / expertise']];
        const missingProfessionalFields = requiredProfessionalFields.filter(([value]) => !value.trim()).map(([, label]) => label);
        if (missingProfessionalFields.length) {
          setError(`Please complete the professional-services information: ${missingProfessionalFields.join(', ')}.`);
          return false;
        }
      }
    }
    if (currentStep === 4) {
      const required: [string, string][] = [[data.bidSecurityAbility, 'Ability to provide Bid Security'], [data.bidSecurityType, 'Type of Bid Security'], [data.complianceRepName, 'Full Legal Name'], [data.complianceRepNationality, 'Nationality'], [data.complianceRepDOB, 'Date of Birth'], [data.complianceRepPlaceOfBirth, 'Place of Birth'], [data.complianceRepIdNumber, 'National ID / Passport Number'], [data.complianceRepCountryOfIssue, 'Country of Issue'], [data.complianceRepFathersName, "Father's Full Name"], [data.complianceRepMothersName, "Mother's Full Name"], [data.complianceRepAddress, 'Residential / Business Address'], [data.complianceRepCountry, 'Country of Residence'], [data.complianceRepPhone, 'Mobile Telephone Number'], [data.complianceRepEmail, 'Email Address'], [data.complianceRepPosition, 'Current Occupation / Position']];
      const missing = required.filter(([value]) => !value.trim()).map(([, label]) => label);
      const missingFiles = identityDocuments.filter(document => !files[document.key]).map(document => document.label);
      if (missing.length || missingFiles.length) {
        setError(`Compliance & Security is compulsory. Please complete: ${[...missing, ...missingFiles].join(', ')}.`);
        return false;
      }
    }
    return true;
  };
  const handleNext = () => {
    if (!validateStep()) return;
    setCurrentStep(step => Math.min(step + 1, steps.length));
  };
  const handleSubmit = async () => {
    setError(null);
    if (!data.confirmAccurate || !data.agreeCodeOfConduct || !data.understandFalseInfo) {
      setError('Please tick all three declaration checkboxes to proceed.');
      return;
    }
    if (!data.declarationName.trim() || !data.signature.trim()) {
      setError('Please provide your name and digital signature in the declaration.');
      return;
    }
    const missingDocs = allDocuments.filter(document => document.required && !files[document.key]).map(document => document.label);
    if (missingDocs.length) {
      setError(`Missing required documents: ${missingDocs.join(', ')}.`);
      return;
    }
    if (attachmentSize() > MAX_TOTAL_FILE_SIZE_MB) {
      setError(`Total attachment size exceeds ${MAX_TOTAL_FILE_SIZE_MB}MB. Please compress documents or photos and try again.`);
      return;
    }
    const payload = new FormData();
    payload.append('_subject', 'New AU Expression of Interest submission');
    payload.append('_captcha', 'false');
    payload.append('_template', 'table');
    const values: Array<[string, string]> = [['Company Name', data.companyName], ['Parent Company', data.parentCompany], ['Company Address', data.address], ['Telephone Number', normalizePhone(data.phone)], ['WhatsApp Number', data.whatsappNumber], ['Company Email', data.email], ['Contact Person', data.contactFullName], ['Contact Person Title', data.contactTitle], ['Type of Organisation', data.typeOfOrganisation], ['Year Established', data.yearEstablished], ['Company Registration Number', data.registrationNumber], ['Type of Opportunity / Contract Interest', data.contractInterest], ['Business Categories', data.categories.map(item => item === 'Other' ? `Other: ${data.otherCategory}` : item).join(', ')], ['Area of Specialization', data.areaOfSpecialization], ['Service / Consulting Discipline', data.professionalDiscipline], ['Relevant Qualifications and Certifications', data.qualificationsCertifications], ['Years of Relevant Experience', data.yearsRelevantExperience], ['Similar Assignment 1 - Client / Country', data.assignmentOneClientCountry], ['Similar Assignment 1 - Period', data.assignmentOnePeriod], ['Similar Assignment 1 - Scope / Contract Value', data.assignmentOneScopeValue], ['Similar Assignment 2 - Client / Country', data.assignmentTwoClientCountry], ['Similar Assignment 2 - Period', data.assignmentTwoPeriod], ['Similar Assignment 2 - Scope / Contract Value', data.assignmentTwoScopeValue], ['Similar Assignment 3 - Client / Country', data.assignmentThreeClientCountry], ['Similar Assignment 3 - Period', data.assignmentThreePeriod], ['Similar Assignment 3 - Scope / Contract Value', data.assignmentThreeScopeValue], ['Key Personnel / Expertise', data.keyPersonnelExpertise], ['Employees Total', data.employeesTotal], ['Employees Full Time', data.employeesFullTime], ['Employees Part Time', data.employeesPartTime], ['Authorised Representative Name', data.authRepName], ['Authorised Representative Title', data.authRepTitle], ['Authorised Representative Telephone', data.authRepPhone], ['Authorised Representative Email', data.authRepEmail], ['Bank Account Name', data.accountName], ['Bank Name', data.bankName], ['Bank Address', data.bankAddress], ['Bank Account Number', data.accountNumber], ['IBAN', data.iban], ['SWIFT / BIC', data.swiftBic], ['Account Currency', data.currency], ['Audited Financial Statements Available', data.auditedFinancials], ['Annual Turnover - Current Year', data.turnoverCurrent], ['Annual Turnover - Previous Year', data.turnoverPrevious], ['Ability to Provide Bid Security', data.bidSecurityAbility], ['Type of Bid Security', data.bidSecurityType], ['Compliance Representative - Full Legal Name', data.complianceRepName], ['Compliance Representative - Nationality', data.complianceRepNationality], ['Compliance Representative - Date of Birth', data.complianceRepDOB], ['Compliance Representative - Place of Birth', data.complianceRepPlaceOfBirth], ['Compliance Representative - ID / Passport Number', data.complianceRepIdNumber], ['Compliance Representative - Country of Issue', data.complianceRepCountryOfIssue], ["Compliance Representative - Father's Full Name", data.complianceRepFathersName], ["Compliance Representative - Mother's Full Name", data.complianceRepMothersName], ['Compliance Representative - Address', data.complianceRepAddress], ['Compliance Representative - Country of Residence', data.complianceRepCountry], ['Compliance Representative - Mobile Telephone', data.complianceRepPhone], ['Compliance Representative - Email', data.complianceRepEmail], ['Compliance Representative - Position', data.complianceRepPosition], ['Declaration', 'Confirmed accurate information | Agreed supplier code of conduct | Acknowledged false information consequences'], ['Declaration Name', data.declarationName], ['Declaration Title', data.declarationTitle], ['Declaration Date', data.declarationDate], ['Digital Signature', data.signature]];
    values.forEach(([name, value]) => payload.append(name, value));
    const uploadedDocumentIndex = allDocuments.map(document => {
      const file = files[document.key];
      if (!file) return null;
      // FormSubmit recognizes files submitted through its standard attachment
      // field. The document label is included separately in the email index.
      payload.append('attachment', file, file.name);
      return `${document.label}: ${file.name}`;
    }).filter((document): document is string => Boolean(document));
    payload.append('Uploaded Documents', uploadedDocumentIndex.join(' | '));
    setIsSubmitting(true);
    try {
      const response = await fetch(FORMSUBMIT_ENDPOINT, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: payload
      });
      if (!response.ok) {
        let message = 'There was a problem submitting your EOI. Please try again.';
        try {
          const result: {
            message?: string;
          } = await response.json();
          if (result.message) message = result.message;
        } catch {
          // Keep the user-friendly default message.
        }
        setError(message);
        return;
      }
      setSubmitted(true);
    } catch {
      setError('We could not connect to the submission service. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const renderTextInput = (id: string, label: string, key: TextKey, type = 'text', required = false) => <div data-id="element-856" data-mp="3vqm77mv7hg1c">
      <label htmlFor={id} className="block text-sm font-medium text-charcoal mb-2" data-id="element-857" data-mp="3ht7fucle6y6q">
        {label} {required && <span className="text-au-red" data-id="element-858" data-mp="3mskavkmf3bke">*</span>}
      </label>
      <input id={id} type={type} value={data[key]} onChange={event => update(key, event.target.value)} className={inputClass} data-id="element-859" data-mp="3mpxv4xpdhpr3" />
    </div>;
  const renderUpload = (document: DocumentDef) => {
    const file = files[document.key];
    return <div key={document.key} className={`border-2 border-dashed rounded-lg p-5 ${file ? 'border-au-green bg-au-green-light' : 'border-gray-border'}`} data-id="element-860" data-mp="161kc57y4zlf1">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-861" data-mp="2vlboe9vvjtmr">
          <div data-id="element-862" data-mp="2qna8tyxnqcg1">
            <p className="text-sm font-medium text-charcoal" data-id="element-863" data-mp="2qniilhyhh1n0">
              {document.label}{' '}
              {document.required && <span className="text-au-red" data-id="element-864" data-mp="30jvemmhi3rkx">*</span>}
            </p>
            {file && <p className="text-xs text-au-green mt-1" data-id="element-865" data-mp="30i23ouzuul74">
                {file.name} · {(file.size / 1048576).toFixed(2)}MB
              </p>}
          </div>
          <div className="flex items-center gap-2" data-id="element-866" data-mp="3a953zukymw5c">
            <input ref={element => {
            fileRefs.current[document.key] = element;
          }} id={`file-${document.key}`} type="file" accept={document.accept} onChange={event => setFiles(previous => ({
            ...previous,
            [document.key]: event.target.files?.[0] ?? null
          }))} className="sr-only" data-id="element-867" data-mp="3o9zhgrmv7nav" />
            <Button type="button" variant="secondary" onClick={() => fileRefs.current[document.key]?.click()} className="flex items-center gap-2 py-2 text-sm" data-id="element-868" data-mp="181zn6j2rxwne">
              <UploadIcon size={16} data-id="element-869" data-mp="1hr1183pre3yq" />
              {file ? 'Replace' : 'Choose File'}
            </Button>
            {file && <button type="button" onClick={() => {
            setFiles(previous => ({
              ...previous,
              [document.key]: null
            }));
            if (fileRefs.current[document.key]) {
              fileRefs.current[document.key]!.value = '';
            }
          }} className="rounded p-2 text-au-red hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-au-green" aria-label={`Remove ${document.label}`} data-id="element-870" data-mp="k652r0adhvgn">
                <XCircleIcon size={20} data-id="element-871" data-mp="3v0v6bnz8jm64" />
              </button>}
          </div>
        </div>
      </div>;
  };
  if (submitted) {
    return <div className="w-full min-h-screen bg-ivory" data-id="element-872" data-mp="3l7xjx6r4hd28">
        <PageHero title="Expression of Interest (EOI)" subtitle="Submit an Expression of Interest to participate in AU procurement opportunities" breadcrumbs={[{
        label: 'Home',
        path: '/'
      }, {
        label: 'Procurement'
      }, {
        label: 'Expression of Interest'
      }]} data-id="element-873" data-mp="3l6mhpbdbsdd8" />
        <main className="max-w-2xl mx-auto px-4 py-16 text-center" data-id="element-874" data-mp="2xbtkevf7sp4k">
          <Card data-id="element-875" data-mp="10iq9wer5ddt2">
            <div className="w-20 h-20 bg-au-green-light rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-876" data-mp="10j9tdzwg4a97">
              <CheckCircleIcon size={48} className="text-au-green" data-id="element-877" data-mp="vkot60h71qkr" />
            </div>
            <h1 className="text-3xl font-serif text-charcoal mb-3" data-id="element-878" data-mp="1lsssf6uxa1r0">
              Expression of Interest Submitted
            </h1>
            <div className="w-16 h-1 bg-au-gold mx-auto mb-6" data-id="element-879" data-mp="1gtybkagimn9j" />
            <p className="text-gray-700 text-lg leading-relaxed mb-6" data-id="element-880" data-mp="1vjvw8gf8kutz">
              Thank you for submitting your Expression of Interest to the
              African Union Commission. Your submission and supporting documents
              have been received for review.
            </p>
            <Button variant="secondary" onClick={() => window.location.href = '/'} data-id="element-881" data-mp="xvvhhh94204v">
              Return to Home
            </Button>
          </Card>
        </main>
      </div>;
  }
  return <div className="w-full min-h-screen bg-ivory" data-id="element-882" data-mp="17t075r260dqf">
      <PageHero title="Expression of Interest (EOI)" subtitle="Submit an Expression of Interest to participate in AU procurement opportunities" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Procurement'
    }, {
      label: 'Expression of Interest'
    }]} data-id="element-883" data-mp="2najrjbu8b5zq" />

      <main className="max-w-4xl mx-auto px-4 py-12" data-id="element-884" data-mp="2gujngd5wyooi">
        <section className="mb-8 rounded-lg border border-au-green/20 bg-au-green-light/50 p-6" data-id="element-885" data-mp="2gw4gtzghs1ks">
          <h1 className="text-xl font-serif text-charcoal mb-3" data-id="element-886" data-mp="2ltmsvypt2wx0">
            Expression of Interest (EOI) Form
          </h1>
          <p className="text-sm leading-relaxed text-gray-700" data-id="element-887" data-mp="39lbgj1romx7m">
            Complete all required information accurately and provide the
            requested supporting documentation for African Union Commission
            procurement opportunities, including goods, works, consultancy and
            non-consulting services.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-888" data-mp="3sgs3fwg3z6ul">
            <Button type="button" variant="secondary" onClick={downloadBlankEOIForm} className="flex items-center gap-2 text-sm" data-id="element-889" data-mp="12vqwiutay68">
              <DownloadIcon size={16} data-id="element-890" data-mp="60avyy6qzglc" />
              Download Blank EOI Form (PDF)
            </Button>
            <span className="text-xs text-gray-600" data-id="element-891" data-mp="1y7jq7c987z0">
              Download a printable working copy before completing the online
              form.
            </span>
          </div>
        </section>

        <FormStepper currentStep={currentStep} steps={steps} data-id="element-892" data-mp="3o5e8v52ygwwb" />

        <Card data-id="element-893" data-mp="3o7qym4eygbjx">
          {currentStep === 1 && <section aria-labelledby="general-heading" data-id="element-894" data-mp="3jepbxa8ib7ps">
              <h2 id="general-heading" className="text-2xl font-bold text-charcoal mb-2" data-id="element-895" data-mp="3jd4hrlq3tqsg">
                SECTION 1: GENERAL INFORMATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-896" data-mp="3acg7n23u0aku" />
              <div className="space-y-5" data-id="element-897" data-mp="35dv6n0gr9mvc">
                {renderTextInput('company-name', '1. Name of Company', 'companyName', 'text', true)}
                {renderTextInput('parent-company', '1.1 Parent Company (if applicable)', 'parentCompany')}
                <div data-id="element-898" data-mp="1pue0z0ajrntt">
                  <label htmlFor="company-address" className="block text-sm font-medium text-charcoal mb-2" data-id="element-899" data-mp="1txk2jumpsqty">
                    2. Full Address of the Company{' '}
                    <span className="text-au-red" data-id="element-900" data-mp="127pf63if3b7x">*</span>
                  </label>
                  <textarea id="company-address" rows={3} value={data.address} onChange={event => update('address', event.target.value)} className={inputClass} data-id="element-901" data-mp="167pqvge4t8ba" />
                </div>
                <div className="grid md:grid-cols-2 gap-5" data-id="element-902" data-mp="ihvfz4ddhtto">
                  <div data-id="element-903" data-mp="igak9bfbo4u8">
                    <label htmlFor="phone" className="block text-sm font-medium text-charcoal mb-2" data-id="element-904" data-mp="3vj68pgv9yy1e">
                      3. Telephone Number <span className="text-au-red" data-id="element-905" data-mp="93v1ui80218v">*</span>
                    </label>
                    <input id="phone" type="tel" value={data.phone} onChange={event => update('phone', event.target.value)} onBlur={() => {
                  const phone = normalizePhone(data.phone);
                  if (/^\+\d{8,15}$/.test(phone)) {
                    update('phone', phone);
                    setPhoneError(null);
                  }
                }} placeholder="+447700900123" aria-invalid={Boolean(phoneError)} className={inputClass} data-id="element-906" data-mp="95ob859zyzkk" />
                    <p className="mt-1 text-xs text-gray-500" data-id="element-907" data-mp="2oxc51rtpsbz5">
                      Use international format beginning with + and country
                      code.
                    </p>
                    {phoneError && <p role="alert" className="mt-1 text-xs text-au-red" data-id="element-908" data-mp="unngxbxi0twx">
                        {phoneError}
                      </p>}
                  </div>
                  <div data-id="element-909" data-mp="lmgygxbah911">
                    <label htmlFor="whatsapp" className="block text-sm font-medium text-charcoal mb-2" data-id="element-910" data-mp="lm7h1y4b8a6y">
                      4. WhatsApp Number <span className="text-au-red" data-id="element-911" data-mp="bot1e0q7npfp">*</span>
                    </label>
                    <input id="whatsapp" type="tel" value={data.whatsappNumber} onChange={event => {
                  update('whatsappNumber', event.target.value);
                  setWhatsappError(null);
                }} placeholder="+447700900123" aria-invalid={Boolean(whatsappError)} className={inputClass} data-id="element-912" data-mp="gnckzuhc0p4w" />
                    <p className="mt-1 text-xs text-gray-500" data-id="element-913" data-mp="1zlsjxf4p12l9">
                      If you do not use WhatsApp, enter your telephone number
                      here instead.
                    </p>
                    {whatsappError && <p role="alert" className="mt-1 text-xs text-au-red" data-id="element-914" data-mp="2sa6fzpc81ou6">
                        {whatsappError}
                      </p>}
                  </div>
                </div>
                {renderTextInput('email', '5. Email Address', 'email', 'email', true)}
                <div className="grid md:grid-cols-2 gap-5" data-id="element-915" data-mp="3b2s5zt0g6l6u">
                  {renderTextInput('contact-name', '6. Name of Contact Person', 'contactFullName', 'text', true)}
                  {renderTextInput('contact-title', 'Title of Contact Person', 'contactTitle', 'text', true)}
                </div>
                <div data-id="element-916" data-mp="1cuwleuo4ug3">
                  <label htmlFor="organisation-type" className="block text-sm font-medium text-charcoal mb-2" data-id="element-917" data-mp="1bjvjmsly0so">
                    7. Type of Organisation{' '}
                    <span className="text-au-red" data-id="element-918" data-mp="a5gg4x64j55z">*</span>
                  </label>
                  <select id="organisation-type" value={data.typeOfOrganisation} onChange={event => update('typeOfOrganisation', event.target.value)} className={inputClass} data-id="element-919" data-mp="f2yg3y25ya1u">
                    <option value="" data-id="element-920" data-mp="1abkv8olzwg1u">Select type</option>
                    <option value="Private Company" data-id="element-921" data-mp="15ekn885l5383">Private Company</option>
                    <option value="State Enterprise" data-id="element-922" data-mp="1jdu3t8xpdzcl">State Enterprise</option>
                    <option value="Individual Consultant" data-id="element-923" data-mp="1jd12wolfe42d">
                      Individual Consultant
                    </option>
                    <option value="Other" data-id="element-924" data-mp="qovjc563yl44">Other</option>
                  </select>
                </div>
                <div className="grid md:grid-cols-2 gap-5" data-id="element-925" data-mp="mntjbm4da513">
                  {renderTextInput('year-established', '8. Year Established', 'yearEstablished', 'text', true)}
                  {renderTextInput('registration-number', '9. Company Registration Number', 'registrationNumber', 'text', true)}
                </div>
                <fieldset className="border-t border-gray-border pt-6" data-id="element-926" data-mp="2ektag4j9oz5s">
                  <legend className="text-sm font-medium text-charcoal mb-2" data-id="element-927" data-mp="2jefpu4gtfh5a">
                    10. Type of opportunity / contract interest{' '}
                    <span className="text-au-red" data-id="element-928" data-mp="2alc5rdbo061o">*</span>
                  </legend>
                  <p className="text-xs text-gray-600 mb-3" data-id="element-929" data-mp="2aiybh6ot6xv6">
                    Select the primary type of opportunity you intend to pursue.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3" data-id="element-930" data-mp="32b77yakmfq7r">
                    {['Goods supply', 'Works', 'Consultancy services', 'Non-consulting services', 'Other'].map(option => <label key={option} className="flex items-center gap-2 rounded-lg border border-gray-border p-3 cursor-pointer" data-id="element-931" data-mp="1cw956kn2pz3i">
                        <input type="radio" name="contract-interest" value={option} checked={data.contractInterest === option} onChange={event => update('contractInterest', event.target.value)} className="w-4 h-4 text-au-green focus:ring-au-green" data-id="element-932" data-mp="107bx84nmkn6n" />
                        <span className="text-sm text-charcoal" data-id="element-933" data-mp="1ny7uviericlt">{option}</span>
                      </label>)}
                  </div>
                </fieldset>
              </div>
            </section>}

          {currentStep === 2 && <section aria-labelledby="business-heading" data-id="element-934" data-mp="3uoe1l2mnmn27">
              <h2 id="business-heading" className="text-2xl font-bold text-charcoal mb-2" data-id="element-935" data-mp="2loakbgz1j8b">
                SECTION 2: ORGANISATION, BUSINESS &amp; CAPABILITY INFORMATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-936" data-mp="3m983xqiwprpw" />
              <div className="space-y-6" data-id="element-937" data-mp="3masz9id1peot">
                <fieldset data-id="element-938" data-mp="4z9y4wp3bcvf">
                  <legend className="text-sm font-medium text-charcoal mb-3" data-id="element-939" data-mp="4zjqus1iry4t">
                    10. Business Category <span className="text-au-red" data-id="element-940" data-mp="1zzqrjq3qvg">*</span>
                  </legend>
                  <div className="grid md:grid-cols-2 gap-3" data-id="element-941" data-mp="38htep1yeouae">
                    {['Manufacturer', 'Trading Company', 'Service Provider', 'Contractor', 'Technology Company', 'Supplier', 'Authorized Agent', 'Individual Consultant / Sole Practitioner', 'Other'].map(category => <label key={category} className="flex items-center gap-2 rounded-lg border border-gray-border p-3 cursor-pointer" data-id="element-942" data-mp="2kqxsqlgmstb2">
                        <input type="checkbox" checked={data.categories.includes(category)} onChange={event => setData(previous => ({
                    ...previous,
                    categories: event.target.checked ? [...previous.categories, category] : previous.categories.filter(item => item !== category),
                    otherCategory: category === 'Other' && !event.target.checked ? '' : previous.otherCategory
                  }))} className="w-4 h-4 text-au-green focus:ring-au-green" data-id="element-943" data-mp="3efdg7164ha6c" />
                        <span className="text-sm text-charcoal" data-id="element-944" data-mp="20a9yv6p0w398">
                          {category}
                        </span>
                      </label>)}
                  </div>
                  {data.categories.includes('Other') && <input type="text" value={data.otherCategory} onChange={event => update('otherCategory', event.target.value)} className={`${inputClass} mt-3`} placeholder="Please specify the other category" aria-label="Specify other business category" data-id="element-945" data-mp="1mhkcv29huyc4" />}
                </fieldset>
                <div data-id="element-946" data-mp="31yu9l63zjviw">
                  <label htmlFor="specialization" className="block text-sm font-medium text-charcoal mb-2" data-id="element-947" data-mp="31znh49arqp23">
                    11. Area of Specialization{' '}
                    <span className="text-au-red" data-id="element-948" data-mp="3exv0vin7sjtb">*</span>
                  </label>
                  <textarea id="specialization" rows={3} value={data.areaOfSpecialization} onChange={event => update('areaOfSpecialization', event.target.value)} className={inputClass} data-id="element-949" data-mp="3002ogzcysbjc" />
                </div>
                <div className="grid md:grid-cols-3 gap-4" data-id="element-950" data-mp="2hb4ovlyvzlh6">
                  {renderTextInput('employee-total', '12. Employees — Total', 'employeesTotal', 'number')}
                  {renderTextInput('employee-full-time', 'Full Time', 'employeesFullTime', 'number')}
                  {renderTextInput('employee-part-time', 'Part Time', 'employeesPartTime', 'number')}
                </div>
                <fieldset className="border-t border-gray-border pt-6" data-id="element-951" data-mp="1xp2s05g50ity">
                  <legend className="text-sm font-medium text-charcoal mb-4" data-id="element-952" data-mp="1xr5sxkitvwdg">
                    13. Authorised Representative
                  </legend>
                  <div className="grid md:grid-cols-2 gap-4" data-id="element-953" data-mp="27lxt8w3soxbx">
                    {renderTextInput('auth-name', 'Name', 'authRepName')}
                    {renderTextInput('auth-title', 'Title', 'authRepTitle')}
                    {renderTextInput('auth-phone', 'Telephone', 'authRepPhone', 'tel')}
                    {renderTextInput('auth-email', 'Email', 'authRepEmail', 'email')}
                  </div>
                </fieldset>

                {(data.contractInterest === 'Consultancy services' || data.contractInterest === 'Non-consulting services') && <section className="border-t border-gray-border pt-6" aria-labelledby="professional-services-heading" data-id="element-954" data-mp="21s8v3oi2tnum">
                    <h3 id="professional-services-heading" className="text-lg font-bold text-charcoal mb-2" data-id="element-955" data-mp="2bovcg3b71wrw">
                      14. Professional Services Capability
                    </h3>
                    <p className="text-sm text-gray-600 mb-5" data-id="element-956" data-mp="2knyz1h9kkub5">
                      Complete this section for consultancy and non-consulting
                      service opportunities. Include assignments delivered by
                      the firm, consortium, or individual consultant, as
                      applicable.
                    </p>

                    <div className="space-y-5" data-id="element-957" data-mp="e1as6slqqyz7">
                      <div className="grid md:grid-cols-2 gap-4" data-id="element-958" data-mp="92zichvg4ogz">
                        {renderTextInput('service-discipline', 'Service / Consulting Discipline', 'professionalDiscipline', 'text', true)}
                        {renderTextInput('years-experience', 'Years of Relevant Experience', 'yearsRelevantExperience', 'number', true)}
                      </div>
                      <div data-id="element-959" data-mp="3chls90zdf6sy">
                        <label htmlFor="qualifications-certifications" className="block text-sm font-medium text-charcoal mb-2" data-id="element-960" data-mp="38icw5pguywu8">
                          Relevant Qualifications and Certifications{' '}
                          <span className="text-au-red" data-id="element-961" data-mp="1ge7c012ycpet">*</span>
                        </label>
                        <textarea id="qualifications-certifications" rows={3} value={data.qualificationsCertifications} onChange={event => update('qualificationsCertifications', event.target.value)} className={inputClass} placeholder="List relevant qualifications, professional registrations, licences and certifications." data-id="element-962" data-mp="12j563dy3v020" />
                      </div>

                      {[1, 2, 3].map(assignmentNumber => {
                  const assignmentFields = [{
                    id: `assignment-${assignmentNumber}-client`,
                    label: 'Client / Country',
                    key: assignmentNumber === 1 ? 'assignmentOneClientCountry' : assignmentNumber === 2 ? 'assignmentTwoClientCountry' : 'assignmentThreeClientCountry'
                  }, {
                    id: `assignment-${assignmentNumber}-period`,
                    label: 'Period',
                    key: assignmentNumber === 1 ? 'assignmentOnePeriod' : assignmentNumber === 2 ? 'assignmentTwoPeriod' : 'assignmentThreePeriod'
                  }, {
                    id: `assignment-${assignmentNumber}-scope`,
                    label: 'Scope / Contract Value',
                    key: assignmentNumber === 1 ? 'assignmentOneScopeValue' : assignmentNumber === 2 ? 'assignmentTwoScopeValue' : 'assignmentThreeScopeValue'
                  }] as {
                    id: string;
                    label: string;
                    key: TextKey;
                  }[];
                  const required = assignmentNumber === 1;
                  return <fieldset key={assignmentNumber} className="rounded-lg border border-gray-border p-4" data-id="element-963" data-mp="2g8avjjg4bxum">
                            <legend className="px-1 text-sm font-medium text-charcoal" data-id="element-964" data-mp="2nls5jq1q0121">
                              Similar Assignment {assignmentNumber}{' '}
                              {required ? '(required)' : '(optional)'}
                            </legend>
                            <div className="grid md:grid-cols-3 gap-4 mt-2" data-id="element-965" data-mp="31kru6xp8gfy7">
                              {assignmentFields.map(field => renderTextInput(field.id, field.label, field.key, 'text', required))}
                            </div>
                          </fieldset>;
                })}

                      <div data-id="element-966" data-mp="3u8faw1ndumfk">
                        <label htmlFor="key-personnel" className="block text-sm font-medium text-charcoal mb-2" data-id="element-967" data-mp="3u5jdcefg5zrn">
                          Key Personnel / Expertise{' '}
                          <span className="text-au-red" data-id="element-968" data-mp="23whgwyxeojjv">*</span>
                        </label>
                        <textarea id="key-personnel" rows={3} value={data.keyPersonnelExpertise} onChange={event => update('keyPersonnelExpertise', event.target.value)} className={inputClass} placeholder="List proposed key personnel, roles and relevant areas of expertise." data-id="element-969" data-mp="1z3odqfhl5j9w" />
                      </div>

                      <div className="rounded-lg border border-dashed border-gray-border p-4" data-id="element-970" data-mp="sidfx8sehv5b">
                        <p className="text-sm font-medium text-charcoal mb-1" data-id="element-971" data-mp="njakmg2833aj">
                          Optional capability evidence
                        </p>
                        <p className="text-xs text-gray-600 mb-3" data-id="element-972" data-mp="342l08v53rz85">
                          Upload one capability statement, CV pack, or
                          supporting portfolio. This counts toward the{' '}
                          {MAX_TOTAL_FILE_SIZE_MB}
                          MB combined upload limit.
                        </p>
                        {renderUpload(companyDocuments.find(document => document.key === 'professionalCapability')!)}
                      </div>
                    </div>
                  </section>}
              </div>
            </section>}

          {currentStep === 3 && <section aria-labelledby="financial-heading" data-id="element-973" data-mp="9nvm0xlzjg0q">
              <h2 id="financial-heading" className="text-2xl font-bold text-charcoal mb-2" data-id="element-974" data-mp="9l9c4rbmgofc">
                SECTION 3: FINANCIAL &amp; BANKING INFORMATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-975" data-mp="373w8uj3jxdks" />
              <div className="space-y-5" data-id="element-976" data-mp="372bf2vp29ynz">
                <div className="grid md:grid-cols-2 gap-4" data-id="element-977" data-mp="325bfn3mpvu5u">
                  {renderTextInput('account-name', 'Account Name', 'accountName')}
                  {renderTextInput('bank-name', 'Bank Name', 'bankName')}
                </div>
                {renderTextInput('bank-address', 'Bank Address', 'bankAddress')}
                <div className="grid md:grid-cols-2 gap-4" data-id="element-978" data-mp="2jf3l39x32c19">
                  {renderTextInput('account-number', 'Account Number', 'accountNumber')}
                  {renderTextInput('iban', 'IBAN', 'iban')}
                  {renderTextInput('swift-bic', 'SWIFT / BIC', 'swiftBic')}
                  {renderTextInput('currency', 'Currency of Account', 'currency')}
                </div>
                <fieldset data-id="element-979" data-mp="yxc7dyk1xhwp">
                  <legend className="text-sm font-medium text-charcoal mb-2" data-id="element-980" data-mp="13vm3qchiiojw">
                    14. Audited Financial Statements Available
                  </legend>
                  <div className="flex gap-5" data-id="element-981" data-mp="69otf2dadpj1">
                    {['Yes', 'No'].map(option => <label key={option} className="flex items-center gap-2 cursor-pointer" data-id="element-982" data-mp="b784xuilno7a">
                        <input type="radio" name="audited" value={option} checked={data.auditedFinancials === option} onChange={event => update('auditedFinancials', event.target.value)} className="w-4 h-4 text-au-green" data-id="element-983" data-mp="kxtci7g9432c" />
                        <span className="text-sm" data-id="element-984" data-mp="3qayt30zfes0k">{option}</span>
                      </label>)}
                  </div>
                </fieldset>
                <div className="grid md:grid-cols-2 gap-4" data-id="element-985" data-mp="3lcf40rpjd03z">
                  {renderTextInput('turnover-current', '15. Annual Turnover — Current Year', 'turnoverCurrent')}
                  {renderTextInput('turnover-previous', 'Annual Turnover — Previous Year', 'turnoverPrevious')}
                </div>
                <section className="border-t border-gray-border pt-6" aria-labelledby="company-documents-heading" data-id="element-986" data-mp="mtgkgrbtxgz3">
                  <h3 id="company-documents-heading" className="text-lg font-bold text-charcoal mb-2" data-id="element-987" data-mp="1zss0ydkobgc1">
                    16. Supporting Documentation
                  </h3>
                  <p className="mb-5 text-sm text-gray-600" data-id="element-988" data-mp="1qt55e8eml4qw">
                    Maximum combined upload size:{' '}
                    <strong data-id="element-989" data-mp="1vrq7kddftyhz">{MAX_TOTAL_FILE_SIZE_MB}MB</strong>. Compress PDFs
                    and photos if required.
                  </p>
                  <div className="space-y-4" data-id="element-990" data-mp="2nicrpin2jhvv">
                    {companyDocuments.filter(document => document.key !== 'professionalCapability').map(renderUpload)}
                  </div>
                  {attachmentSize() > MAX_TOTAL_FILE_SIZE_MB && <div role="alert" className="mt-4 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-id="element-991" data-mp="32e28919hj2t7">
                      <AlertCircleIcon size={18} className="shrink-0 mt-0.5" data-id="element-992" data-mp="175ory3vs60zm" />
                      <span data-id="element-993" data-mp="12ccfst8afmnr">
                        Total attachment size exceeds {MAX_TOTAL_FILE_SIZE_MB}
                        MB. Please compress documents or photos.
                      </span>
                    </div>}
                </section>
              </div>
            </section>}

          {currentStep === 4 && <section aria-labelledby="compliance-heading" data-id="element-994" data-mp="1l3vhmwqksuo7">
              <h2 id="compliance-heading" className="text-2xl font-bold text-charcoal mb-2" data-id="element-995" data-mp="1l4en3dz1hqlc">
                SECTION 4: COMPLIANCE &amp; SECURITY REQUIREMENTS{' '}
                <span className="text-au-red" data-id="element-996" data-mp="1c2xdmeg50n5m">(COMPULSORY)</span>
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-997" data-mp="4r6nts8neov8" />
              <div className="space-y-7" data-id="element-998" data-mp="nifv8g3tr9g">
                <div className="rounded-lg border border-au-green/20 bg-au-green-light/50 p-5 text-sm leading-relaxed text-gray-800" data-id="element-999" data-mp="mgt46pq7gbw">
                  <strong data-id="element-1000" data-mp="2gcyr06yayng3">This section is mandatory.</strong> All bidders and
                  suppliers are required to complete it fully. Failure to
                  provide the required information may result in the application
                  not being considered.
                </div>
                <fieldset data-id="element-1001" data-mp="2q4mq8u13p8s4">
                  <legend className="text-lg font-bold text-charcoal mb-3" data-id="element-1002" data-mp="2v2ef1f6g8es3">
                    1. Bid Security
                  </legend>
                  <div className="space-y-4" data-id="element-1003" data-mp="2zzekpozdzfio">
                    <div data-id="element-1004" data-mp="342t2ak2z43y8">
                      <span className="block text-sm font-medium text-charcoal mb-2" data-id="element-1005" data-mp="342b6fok83rqu">
                        Ability to provide Bid Security{' '}
                        <span className="text-au-red" data-id="element-1006" data-mp="38zjqxvsug7v9">*</span>
                      </span>
                      <div className="flex gap-5" data-id="element-1007" data-mp="19qd2o24ono2c">
                        {['Yes', 'No'].map(option => <label key={option} className="flex items-center gap-2 cursor-pointer" data-id="element-1008" data-mp="1epg6xkcu9q99">
                            <input type="radio" name="security" value={option} checked={data.bidSecurityAbility === option} onChange={event => update('bidSecurityAbility', event.target.value)} className="w-4 h-4 text-au-green" data-id="element-1009" data-mp="13g96g0bd8uog" />
                            <span className="text-sm" data-id="element-1010" data-mp="1r6bum5c5tghs">{option}</span>
                          </label>)}
                      </div>
                    </div>
                    {renderTextInput('security-type', 'Type of Bid Security', 'bidSecurityType', 'text', true)}
                  </div>
                </fieldset>

                <section className="border-t border-gray-border pt-6" aria-labelledby="representative-heading" data-id="element-1011" data-mp="2a9dr2objw2jq">
                  <h3 id="representative-heading" className="text-lg font-bold text-charcoal mb-2" data-id="element-1012" data-mp="21a0rqoh54ucq">
                    2. Authorised Representative Verification
                  </h3>
                  <p className="mb-5 text-sm text-gray-600" data-id="element-1013" data-mp="2tyo4twrqkdf7">
                    This information is required for identity verification and
                    compliance purposes.
                  </p>
                  <div className="grid md:grid-cols-2 gap-5" data-id="element-1014" data-mp="2jzgwxp3rtazh">
                    {renderTextInput('rep-name', 'Full Legal Name', 'complianceRepName', 'text', true)}
                    {renderTextInput('rep-nationality', 'Nationality', 'complianceRepNationality', 'text', true)}
                    {renderTextInput('rep-dob', 'Date of Birth', 'complianceRepDOB', 'date', true)}
                    {renderTextInput('rep-birthplace', 'Place of Birth', 'complianceRepPlaceOfBirth', 'text', true)}
                    {renderTextInput('rep-id', 'National Identification / Passport Number', 'complianceRepIdNumber', 'text', true)}
                    <div data-id="element-1015" data-mp="3pvu92htbe5gu">
                      <label htmlFor="issue-country" className="block text-sm font-medium text-charcoal mb-2" data-id="element-1016" data-mp="3px5e0kyhxj9j">
                        Country of Issue <span className="text-au-red" data-id="element-1017" data-mp="3lrfdcw9ftm4">*</span>
                      </label>
                      <select id="issue-country" value={data.complianceRepCountryOfIssue} onChange={event => update('complianceRepCountryOfIssue', event.target.value)} className={inputClass} data-id="element-1018" data-mp="3uwi0v5pdaqc4">
                        <option value="" data-id="element-1019" data-mp="veiga0ow26hp">Select country</option>
                        {countries.map(country => <option key={country.code} value={country.name} data-id="element-1020" data-mp="rezw574w5zfx">
                            {country.name}
                          </option>)}
                      </select>
                    </div>
                    {renderTextInput('rep-father', "Father's Full Name", 'complianceRepFathersName', 'text', true)}
                    {renderTextInput('rep-mother', "Mother's Full Name", 'complianceRepMothersName', 'text', true)}
                    <div className="md:col-span-2" data-id="element-1021" data-mp="2by3lnzlfocz8">
                      {renderTextInput('rep-address', 'Residential / Business Address', 'complianceRepAddress', 'text', true)}
                    </div>
                    {renderTextInput('rep-residence', 'Country of Residence', 'complianceRepCountry', 'text', true)}
                    {renderTextInput('rep-position', 'Current Occupation / Position', 'complianceRepPosition', 'text', true)}
                    {renderTextInput('rep-phone', 'Mobile Telephone Number', 'complianceRepPhone', 'tel', true)}
                    {renderTextInput('rep-email', 'Email Address', 'complianceRepEmail', 'email', true)}
                  </div>
                </section>

                <section className="border-t border-gray-border pt-6" aria-labelledby="identity-heading" data-id="element-1022" data-mp="2wfew40dfpqr6">
                  <h3 id="identity-heading" className="text-lg font-bold text-charcoal mb-2" data-id="element-1023" data-mp="2nhw4gd5ace6b">
                    3. Identity Documents{' '}
                    <span className="text-au-red" data-id="element-1024" data-mp="3g6jxiuwa07uf">(Mandatory)</span>
                  </h3>
                  <p className="mb-5 text-sm text-gray-600" data-id="element-1025" data-mp="3k7dh5c2or0hi">
                    Upload a clear government-issued identification document and
                    a recent passport-sized photograph.
                  </p>
                  <div className="space-y-4" data-id="element-1026" data-mp="3b54e179qy9e5">
                    {identityDocuments.map(renderUpload)}
                  </div>
                </section>
              </div>
            </section>}

          {currentStep === 5 && <section aria-labelledby="declaration-heading" data-id="element-1027" data-mp="1s7pu226iv2ks">
              <h2 id="declaration-heading" className="text-2xl font-bold text-charcoal mb-2" data-id="element-1028" data-mp="1s66c8757wjgw">
                DECLARATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-1029" data-mp="2174kj1movd5a" />
              <div className="space-y-6" data-id="element-1030" data-mp="1w9ktt28t17wg">
                <div className="rounded-lg border border-au-green/20 bg-au-green-light/50 p-6 text-sm leading-relaxed text-gray-800" data-id="element-1031" data-mp="1wamgk3z6niu0">
                  I hereby certify that the information provided in this
                  Expression of Interest form and all supporting documentation
                  is true, complete and accurate. I understand that this
                  Expression of Interest does not constitute a binding contract
                  and that supplier approval is subject to the African Union
                  Commission&apos;s evaluation and procurement procedures.
                </div>
                <fieldset className="space-y-3" data-id="element-1032" data-mp="ktmp1aknt811">
                  <legend className="sr-only" data-id="element-1033" data-mp="8ugdtlhvqwi">Required declarations</legend>
                  {[['confirmAccurate', 'I confirm that all information provided is accurate and complete.'], ['agreeCodeOfConduct', 'I agree to the African Union Supplier Code of Conduct.'], ['understandFalseInfo', 'I understand that providing false information may result in disqualification.']].map(([key, label]) => <label key={key} className="flex items-start gap-3 cursor-pointer" data-id="element-1034" data-mp="wyllemrewwgk">
                      <input type="checkbox" checked={data[key as keyof Pick<FormData, 'confirmAccurate' | 'agreeCodeOfConduct' | 'understandFalseInfo'>]} onChange={event => setData(previous => ({
                  ...previous,
                  [key]: event.target.checked
                }))} className="mt-1 w-4 h-4 text-au-green focus:ring-au-green" data-id="element-1035" data-mp="11yzn7n2f7a65" />
                      <span className="text-sm text-gray-700" data-id="element-1036" data-mp="30dovi4ylrb9f">{label}</span>
                    </label>)}
                </fieldset>
                <div className="grid md:grid-cols-2 gap-5" data-id="element-1037" data-mp="3swcb54utxy34">
                  {renderTextInput('declaration-name', 'Name', 'declarationName', 'text', true)}
                  {renderTextInput('declaration-title', 'Title', 'declarationTitle')}
                  {renderTextInput('declaration-date', 'Date', 'declarationDate', 'date')}
                  {renderTextInput('signature', 'Signature', 'signature', 'text', true)}
                </div>
                {error && <div role="alert" className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-id="element-1038" data-mp="1hdmskwzxlqt7">
                    <AlertCircleIcon size={18} className="shrink-0 mt-0.5" data-id="element-1039" data-mp="1qf4ben4q46ll" />
                    <span data-id="element-1040" data-mp="1qd019ng8ebcv">{error}</span>
                  </div>}
              </div>
            </section>}

          {currentStep !== 5 && error && <div role="alert" className="mt-6 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-id="element-1041" data-mp="2eyxzevgamrby">
              <AlertCircleIcon size={18} className="shrink-0 mt-0.5" data-id="element-1042" data-mp="250ia8y0tts53" />
              <span data-id="element-1043" data-mp="1raerwi52iikx">{error}</span>
            </div>}

          <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-gray-border pt-6" data-id="element-1044" data-mp="1r820zfhc0xvq">
            <Button type="button" variant="secondary" onClick={() => {
            setError(null);
            setCurrentStep(step => Math.max(1, step - 1));
          }} disabled={currentStep === 1 || isSubmitting} data-id="element-1045" data-mp="1hch57z2uwtuf">
              Previous
            </Button>
            {currentStep < steps.length ? <Button type="button" onClick={handleNext} data-id="element-1046" data-mp="3ez9kj8ctohk6">
                Next Step
              </Button> : <div className="sm:text-right" data-id="element-1047" data-mp="3a8kw61wfw0p9">
                <Button type="button" onClick={handleSubmit} disabled={isSubmitting} className="w-full sm:w-auto flex items-center justify-center gap-2" data-id="element-1048" data-mp="3a6q7cci1rofw">
                  {isSubmitting ? 'Submitting…' : <>
                      <CheckCircleIcon size={20} data-id="element-1049" data-mp="3t0m0vkvwal6l" />
                      Submit Expression of Interest
                    </>}
                </Button>
                <p className="mt-3 max-w-md text-xs text-gray-500 sm:ml-auto" role="note" data-id="element-1050" data-mp="3jz5ox8pfmb09">
                  Administrative note: The first live test submission requires
                  the procurement inbox to confirm FormSubmit activation once.
                </p>
              </div>}
          </div>
        </Card>
      </main>
    </div>;
}